import { useChunkUpload } from '@core/shared';
import { BaseApiResponse } from '@core/swagger-client';
import { Button, ErrorMessage, Heading, Upload, useFileDropzone } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const UploadVideoOrganism = ({ onClose }: { onClose: () => void }) => {
    const { t } = useTranslation();
    const { webinarId } = useParams({ strict: false });
    const { upload, cancel, pause, resume, isDone, canResume, isUploading, progress } = useChunkUpload(`/api/video-editor/${webinarId}/upload`);

    const [uploadError, setUploadError] = useState<BaseApiResponse | null>(null);
    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: async (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                const response = await upload(acceptedFiles[0]);
                setUploadError(response);
            }
        },
        accept: {
            'video/x-flv': ['.flv'],
            'video/mp4': ['.mp4'],
            'video/quicktime': ['.mov', '.qt'],
            'video/x-matroska': ['.mkv'],
            'video/webm': ['.webm'],
            'video/avi': ['.avi'],
            'video/mpeg': ['.mpeg', '.mpg'],
            'video/3gpp': ['.3gp', '.3g2'],
            'video/x-ms-wmv': ['.wmv'],
            'video/h264': ['.h264']
        }
    });

    useEffect(() => {
        if (isDone) {
            onClose();
        }
    }, [isDone]);

    const resumeUpload = async () => {
        const response = await resume();
        setUploadError(response);
    };

    const pauseUpload = () => {
        pause();
    };

    const cancelUpload = () => {
        cancel();
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-primary/95 flex overflow-auto z-50 popup-sm-wrapper">
            <div className="relative w-full max-w-2xl bg-white rounded-lg flex-col flex m-auto opacity-100 p-8 popup-sm" role="dialog" aria-modal="true">
                <Heading variant="h2" classNames="mb-6">
                    {t('Editor.Upload.Heading')}
                </Heading>
                {isUploading && (
                    <div className="relative flex-1 flex flex-col items-center justify-center bg-white border-dashed border-2 border-gray-400 rounded-lg p-6">
                        <label htmlFor="progress-bar" className="mb-2">
                            {t('Editor.Upload.Progress.Label')}
                        </label>
                        <progress id="progress-bar" className="progress" value={progress} max="100">
                            {progress}%
                        </progress>
                        <div className="flex gap-4 mt-4">
                            <Button type="button" variant="primary" onClick={pauseUpload}>
                                {t('Editor.Upload.Button.Pause')}
                            </Button>
                            <Button type="button" variant="warning" onClick={cancelUpload}>
                                {t('Editor.Upload.Button.Cancel')}
                            </Button>
                        </div>
                    </div>
                )}
                {canResume && !isUploading && !isDone && (
                    <div className="relative flex-1 flex flex-col items-center justify-center bg-white border-dashed border-2 border-gray-400 rounded-lg p-6">
                        {uploadError && !uploadError.success && <ErrorMessage apiError={uploadError} classNames="mt-4" />}
                        {(!uploadError || uploadError.success) && <p className="mb-4">{t('Editor.Upload.Paused')}</p>}
                        <div className="flex gap-4">
                            <Button type="button" variant="primary" onClick={resumeUpload}>
                                {t('Editor.Upload.Button.Resume')}
                            </Button>
                            <Button type="button" variant="warning" onClick={cancelUpload}>
                                {t('Editor.Upload.Button.Cancel')}
                            </Button>
                        </div>
                    </div>
                )}
                {!canResume && !isUploading && !isDone && (
                    <>
                        <div
                            {...getRootProps()}
                            className={`group w-full ${isDragActive ? 'bg-accent-lighter' : 'bg-white'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-dashed border-2 ${isDragActive ? 'border-accent' : 'border-gray-400'} ${(rejectionCode || uploadError) && !isDragActive && 'border-warning'} hover:border-accent focus-visible:border-accent rounded-lg flex flex-col items-center justify-center p-6 outline-hidden transition-color duration-150 ease-in-out`}
                        >
                            <input data-testid="drop-input" {...getInputProps()} name="videoFile" />
                            <div data-testid="dragAndDrop" className="flex flex-col items-center ">
                                <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                                <p className="font-accent font-w-heading text-3xl mb-2">
                                    {isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}
                                </p>
                                <p>
                                    <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                                </p>
                                <p className="mt-4">
                                    {t('Generic.DragDrop.AcceptedText')}
                                    <span className="font-medium">{t('Editor.Upload.DragDrop.AcceptedFileTypes')}</span>
                                </p>
                            </div>
                            {rejectionCode ? (
                                <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />
                            ) : (
                                uploadError && !uploadError.success && <ErrorMessage apiError={uploadError} classNames="mt-4" />
                            )}
                        </div>
                        <Button classNames="mt-4" type="button" variant="primary" onClick={onClose}>
                            {t('Generic.Dialog.Button.Close')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
